import { useState, useMemo } from 'react'
import { useDashReport } from '@/modules/dash/hooks/useDashReport'

const NOMBRES_BASES: Record<string, string> = {
  'EG COL': 'Enterogermina CO',
  'EG ECU': 'Enterogermina EC',
  'Infant ECU': 'EnteroInfant EC',
}

const ITEMS_POR_PAGINA = 20

interface BaseItem {
  base: string
  total: number
  en_base: number
  porcentaje: number
}

interface EspecialidadItem {
  especialidad: string
  total: number
  en_base: number
  porcentaje: number
}

interface RegionItem {
  region: string
  total: number
  en_base: number
  porcentaje: number
}

interface MedicoItem {
  nombre: string
  especialidad: string
  region: string
  ciudad: string
  marca: string
  sku: string
  prescrip: number
  en_base: string
  base: string
}

interface OpellaProbioticosCruceData {
  titulo: string
  subtitulo: string
  total_medicos: number
  en_base: number
  total_bases: number
  total_especialidades: number
  total_regiones: number
  bases: BaseItem[]
  especialidades: EspecialidadItem[]
  regiones_top: RegionItem[]
  todas_bases: string[]
  todas_especialidades: string[]
  todas_regiones: string[]
  medicos: MedicoItem[]
}

function esEnBase(valor: string): boolean {
  const v = valor.toLowerCase().trim()
  return v === 'sí' || v === 'si'
}

export function OpellaProbioticosCrucePage() {
  const { data, isLoading, error } = useDashReport<OpellaProbioticosCruceData>('opella/probioticos-cruce')
  const [searchTerm, setSearchTerm] = useState('')
  const [filterBase, setFilterBase] = useState('')
  const [filterEspecialidad, setFilterEspecialidad] = useState('')
  const [filterRegion, setFilterRegion] = useState('')
  const [medicosExpanded, setMedicosExpanded] = useState(true)
  const [paginaActual, setPaginaActual] = useState(1)

  const medicosFiltrados = useMemo(() => {
    if (!data) return []
    return data.medicos.filter(medico => {
      const matchNombre = searchTerm === '' || medico.nombre.toLowerCase().includes(searchTerm.toLowerCase().trim())
      const matchBase = filterBase === '' || medico.base === filterBase
      const matchEspecialidad = filterEspecialidad === '' || medico.especialidad === filterEspecialidad
      const matchRegion = filterRegion === '' || medico.region === filterRegion
      return matchNombre && matchBase && matchEspecialidad && matchRegion
    })
  }, [data, searchTerm, filterBase, filterEspecialidad, filterRegion])

  // Compute stats from filtered data
  const computed = useMemo(() => {
    if (!data) return null

    const totalMedicos = medicosFiltrados.length
    const enBase = medicosFiltrados.filter(m => esEnBase(m.en_base)).length
    const totalMedicosGlobal = data.medicos.length

    // Bases únicas
    const basesUnicas = [...new Set(medicosFiltrados.map(m => m.base))]
    const totalBases = basesUnicas.length

    // Especialidades únicas
    const especialidadesUnicas = [...new Set(medicosFiltrados.map(m => m.especialidad))]
    const totalEspecialidades = especialidadesUnicas.length

    // Regiones únicas
    const regionesUnicas = [...new Set(medicosFiltrados.map(m => m.region))]
    const totalRegiones = regionesUnicas.length

    // Calcular agrupaciones
    function calcularAgrupacion(campo: 'base' | 'especialidad' | 'region') {
      const grupos: Record<string, { total: number; en_base: number }> = {}
      medicosFiltrados.forEach(item => {
        const valor = item[campo]
        if (!grupos[valor]) {
          grupos[valor] = { total: 0, en_base: 0 }
        }
        grupos[valor].total++
        if (esEnBase(item.en_base)) {
          grupos[valor].en_base++
        }
      })
      return Object.entries(grupos)
        .map(([nombre, g]) => ({
          nombre,
          total: g.total,
          en_base: g.en_base,
          porcentaje: ((g.total / totalMedicosGlobal) * 100).toFixed(1),
        }))
        .sort((a, b) => b.total - a.total)
    }

    const statsBases = calcularAgrupacion('base')
    const statsEspecialidades = calcularAgrupacion('especialidad')
    const statsRegiones = calcularAgrupacion('region').slice(0, 10)

    // Available filter options (context-dependent)
    const medicosParaBase = data.medicos.filter(m => {
      const matchNombre = searchTerm === '' || m.nombre.toLowerCase().includes(searchTerm.toLowerCase().trim())
      const matchEsp = filterEspecialidad === '' || m.especialidad === filterEspecialidad
      const matchReg = filterRegion === '' || m.region === filterRegion
      return matchNombre && matchEsp && matchReg
    })
    const basesDisponibles = [...new Set(medicosParaBase.map(m => m.base))].sort()

    const medicosParaEsp = data.medicos.filter(m => {
      const matchNombre = searchTerm === '' || m.nombre.toLowerCase().includes(searchTerm.toLowerCase().trim())
      const matchBase = filterBase === '' || m.base === filterBase
      const matchReg = filterRegion === '' || m.region === filterRegion
      return matchNombre && matchBase && matchReg
    })
    const espDisponibles = [...new Set(medicosParaEsp.map(m => m.especialidad))].sort()

    const medicosParaReg = data.medicos.filter(m => {
      const matchNombre = searchTerm === '' || m.nombre.toLowerCase().includes(searchTerm.toLowerCase().trim())
      const matchBase = filterBase === '' || m.base === filterBase
      const matchEsp = filterEspecialidad === '' || m.especialidad === filterEspecialidad
      return matchNombre && matchBase && matchEsp
    })
    const regDisponibles = [...new Set(medicosParaReg.map(m => m.region))].sort()

    return {
      totalMedicos,
      enBase,
      totalBases,
      totalEspecialidades,
      totalRegiones,
      statsBases,
      statsEspecialidades,
      statsRegiones,
      basesDisponibles,
      espDisponibles,
      regDisponibles,
    }
  }, [data, medicosFiltrados, searchTerm, filterBase, filterEspecialidad, filterRegion])

  function handleFilterChange() {
    setPaginaActual(1)
  }

  // Pagination
  const totalPaginas = Math.ceil(medicosFiltrados.length / ITEMS_POR_PAGINA)
  const inicio = (paginaActual - 1) * ITEMS_POR_PAGINA
  const fin = Math.min(inicio + ITEMS_POR_PAGINA, medicosFiltrados.length)
  const medicosPagina = medicosFiltrados.slice(inicio, fin)

  function getBaseColor(base: string): string {
    if (base === 'EG COL') return 'bg-blue-100 text-blue-800'
    if (base === 'EG ECU') return 'bg-teal-100 text-teal-800'
    if (base === 'Infant ECU') return 'bg-pink-100 text-pink-800'
    return 'bg-blue-100 text-blue-800'
  }

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !data || !computed) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1 lg:mb-2">{data.titulo}</h1>
                <p className="text-sm lg:text-base text-gray-600">{data.subtitulo}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a
                href="https://enterprise.tonalstrategies.com/pricing/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
              >
                Calculador
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Filtros Globales */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filtros
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <input
              type="text"
              placeholder="Buscar médico..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); handleFilterChange() }}
              className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
            />

            <select
              value={filterBase}
              onChange={(e) => { setFilterBase(e.target.value); handleFilterChange() }}
              className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
            >
              <option value="">Todas las bases</option>
              {computed.basesDisponibles.map(base => (
                <option key={base} value={base}>
                  {NOMBRES_BASES[base] || base}
                </option>
              ))}
            </select>

            <select
              value={filterEspecialidad}
              onChange={(e) => { setFilterEspecialidad(e.target.value); handleFilterChange() }}
              className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
            >
              <option value="">Todas las especialidades</option>
              {computed.espDisponibles.map(esp => (
                <option key={esp} value={esp}>{esp}</option>
              ))}
            </select>

            <select
              value={filterRegion}
              onChange={(e) => { setFilterRegion(e.target.value); handleFilterChange() }}
              className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
            >
              <option value="">Todas las regiones</option>
              {computed.regDisponibles.map(reg => (
                <option key={reg} value={reg}>{reg}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Estadísticas Generales */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 lg:p-6 border border-blue-200 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Resumen General
          </h2>

          {/* Totales */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-900">{computed.totalMedicos}</p>
              <p className="text-sm text-gray-600 mt-1">Total Médicos</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-green-600">{computed.enBase}</p>
              <p className="text-sm text-gray-600 mt-1">En Base</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-orange-600">{computed.totalBases}</p>
              <p className="text-sm text-gray-600 mt-1">Bases</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-indigo-600">{computed.totalEspecialidades}</p>
              <p className="text-sm text-gray-600 mt-1">Especialidades</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-purple-600">{computed.totalRegiones}</p>
              <p className="text-sm text-gray-600 mt-1">Regiones</p>
            </div>
          </div>
        </div>

        {/* Análisis por Base */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-orange-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            Distribución por Base
          </h2>

          <div className="overflow-x-auto">
            <table className="min-w-full bg-white text-xs lg:text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">Base</th>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Total</th>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">En Base</th>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {computed.statsBases.map((base) => (
                  <tr key={base.nombre} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{NOMBRES_BASES[base.nombre] || base.nombre}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{base.total}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center">
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">{base.en_base}</span>
                    </td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{base.porcentaje}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Análisis por Especialidad */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Distribución por Especialidad
          </h2>

          <div className="overflow-x-auto">
            <table className="min-w-full bg-white text-xs lg:text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">Especialidad</th>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Total</th>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">En Base</th>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {computed.statsEspecialidades.map((esp) => (
                  <tr key={esp.nombre} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{esp.nombre}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{esp.total}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center">
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">{esp.en_base}</span>
                    </td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{esp.porcentaje}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Análisis por Región (Top 10) */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Top 10 Regiones
          </h2>

          <div className="overflow-x-auto">
            <table className="min-w-full bg-white text-xs lg:text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">Región</th>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Total</th>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">En Base</th>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {computed.statsRegiones.map((reg) => (
                  <tr key={reg.nombre} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{reg.nombre}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{reg.total}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center">
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">{reg.en_base}</span>
                    </td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{reg.porcentaje}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Tabla de Médicos */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setMedicosExpanded(!medicosExpanded)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-indigo-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-indigo-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="text-base lg:text-lg">Listado Completo de Médicos</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">({medicosFiltrados.length} médicos)</span>
              </div>
              <svg
                className={`w-6 h-6 lg:w-5 lg:h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${medicosExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {medicosExpanded && (
            <div className="mt-4">
              {/* Tabla */}
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white text-xs lg:text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700 w-16">#</th>
                      <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">Médico</th>
                      <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Base</th>
                      <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Especialidad</th>
                      <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Región</th>
                      <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Ciudad</th>
                      <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Marca</th>
                      <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Prescrip.</th>
                      <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">En Base</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {medicosPagina.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="px-4 py-8 text-center text-gray-500">No se encontraron médicos</td>
                      </tr>
                    ) : (
                      medicosPagina.map((medico, index) => {
                        const numeroFila = inicio + index + 1
                        const enBaseVal = esEnBase(medico.en_base)
                        const enBaseClass = enBaseVal ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        const enBaseText = enBaseVal ? 'Sí' : 'No'
                        const baseColor = getBaseColor(medico.base)

                        return (
                          <tr key={`${medico.nombre}-${numeroFila}`} className="hover:bg-gray-50 transition-colors duration-150">
                            <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-500">{numeroFila}</td>
                            <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{medico.nombre}</td>
                            <td className="px-3 py-2 lg:px-4 lg:py-3 text-center">
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${baseColor}`}>{NOMBRES_BASES[medico.base] || medico.base}</span>
                            </td>
                            <td className="px-3 py-2 lg:px-4 lg:py-3 text-center">
                              <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800">{medico.especialidad}</span>
                            </td>
                            <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{medico.region}</td>
                            <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{medico.ciudad}</td>
                            <td className="px-3 py-2 lg:px-4 lg:py-3 text-center">
                              <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">{medico.marca}</span>
                            </td>
                            <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{medico.prescrip}</td>
                            <td className="px-3 py-2 lg:px-4 lg:py-3 text-center">
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${enBaseClass}`}>{enBaseText}</span>
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Mostrando {medicosFiltrados.length > 0 ? inicio + 1 : 0}-{fin} de {medicosFiltrados.length}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPaginaActual(1)}
                    disabled={paginaActual === 1}
                    className="px-2 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Ir al inicio"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setPaginaActual(p => Math.max(1, p - 1))}
                    disabled={paginaActual === 1}
                    className="px-2 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Anterior"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span className="px-2 py-1.5 text-sm font-medium text-gray-700">{paginaActual}</span>
                  <span className="text-sm text-gray-500">de</span>
                  <span className="px-2 py-1.5 text-sm font-medium text-gray-700">{totalPaginas || 1}</span>
                  <button
                    onClick={() => setPaginaActual(p => Math.min(totalPaginas, p + 1))}
                    disabled={paginaActual >= totalPaginas}
                    className="px-2 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Siguiente"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setPaginaActual(totalPaginas)}
                    disabled={paginaActual >= totalPaginas}
                    className="px-2 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Ir al final"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 text-center text-gray-600 text-xs lg:text-sm">
          <p>&copy; 2026 Tonal Strategies - Análisis de Base de Datos</p>
        </div>

      </div>
    </div>
  )
}
